import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Notify } from '../../Utility/Notify';
import { UpdateOneCampaignData } from '../../Redux/Actions/Campaign_Actions';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconEdit = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const IconWarning = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconCheck = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

/* ─── CSS ────────────────────────────────────────────────────────────────── */
const CSS = `
  /* ── Action Buttons ── */
  .ct-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 8px;
    border: 1.5px solid transparent; cursor: pointer;
    background: transparent; text-decoration: none;
    transition: all 0.18s ease; color: #7B829A;
  }
  .ct-btn:hover { transform: translateY(-1px); }

  .ct-btn.edit {
    border-color: #e2e8f0; background: #f8fafc; color: #2563eb;
  }
  .ct-btn.edit:hover {
    background: #eff6ff; border-color: #2563eb;
  }
  .ct-btn.del {
    border-color: #fee2e2; background: #fff5f5; color: #ef4444;
  }
  .ct-btn.del:hover {
    background: #fee2e2; border-color: #ef4444;
  }

  /* dark mode buttons */
  .dark-btn-edit {
    border-color: #2d3248 !important; background: #1e2132 !important; color: #3b82f6 !important;
  }
  .dark-btn-edit:hover {
    background: #1a2a4a !important; border-color: #3b82f6 !important;
  }
  .dark-btn-del {
    border-color: #3a2525 !important; background: #2a1a1a !important; color: #f87171 !important;
  }
  .dark-btn-del:hover {
    background: #3d1f1f !important; border-color: #f87171 !important;
  }

  /* ── Overlay ── */
  .ct-overlay {
    position: fixed; inset: 0; z-index: 999999;
    display: flex; align-items: center; justify-content: center;
    background: rgba(0,0,0,0.70);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    animation: ct-fadein 0.2s ease-out both;
    padding: 20px;
  }
  @keyframes ct-fadein { from { opacity: 0; } to { opacity: 1; } }

  /* ── Modal Card ── */
  .ct-card {
    width: 100%; max-width: 460px;
    border-radius: 24px; padding: 40px 36px 36px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    box-shadow: 0 32px 80px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.04);
    position: relative; text-align: center;
    animation: ct-slidein 0.28s cubic-bezier(0.34,1.15,0.64,1) both;
    font-family: 'DM Sans', 'Segoe UI', sans-serif;
  }
  .ct-card.dark-m {
    background: #1e2132;
    border-color: #2a2f45;
    box-shadow: 0 32px 80px rgba(0,0,0,0.55);
  }
  @keyframes ct-slidein {
    from { opacity: 0; transform: translateY(24px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* ── Close Button ── */
  .ct-close {
    position: absolute; top: 16px; right: 16px;
    width: 34px; height: 34px; border-radius: 10px;
    border: 1.5px solid #e2e8f0; background: #f8fafc;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    color: #64748b; transition: all 0.2s;
  }
  .ct-close:hover { background: #fee2e2; border-color: #ef4444; color: #ef4444; transform: scale(1.05); }
  .dark-m .ct-close { background: #252d45; border-color: #2d3248; color: #9ca3af; }
  .dark-m .ct-close:hover { background: #3d1f1f; border-color: #f87171; color: #f87171; }

  /* ── Icon Circle ── */
  .ct-icon-wrap {
    width: 80px; height: 80px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 20px;
  }
  .ct-icon-wrap.danger {
    background: linear-gradient(135deg, #fff1f2, #fee2e2);
    border: 2px solid #fca5a5; color: #ef4444;
    box-shadow: 0 8px 24px rgba(239,68,68,0.16);
  }
  .ct-icon-wrap.success-i {
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    border: 2px solid #86efac; color: #16a34a;
    box-shadow: 0 8px 24px rgba(22,163,74,0.16);
    animation: ct-pulse 0.4s ease both;
  }
  .dark-m .ct-icon-wrap.danger  { background: linear-gradient(135deg,#2a1a1a,#3a2020); border-color: #7f1d1d; }
  .dark-m .ct-icon-wrap.success-i { background: linear-gradient(135deg,#052e16,#14532d); border-color: #166534; }
  @keyframes ct-pulse {
    0%   { transform: scale(0.75); opacity: 0; }
    60%  { transform: scale(1.08); }
    100% { transform: scale(1); opacity: 1; }
  }

  /* ── Typography ── */
  .ct-title {
    font-size: 20px; font-weight: 700; letter-spacing: -0.3px;
    color: #0f172a; margin: 0 0 8px;
  }
  .dark-m .ct-title { color: #f1f5f9; }
  .ct-title.success-color { color: #16a34a !important; }
  .dark-m .ct-title.success-color { color: #4ade80 !important; }

  .ct-sub {
    font-size: 13.5px; color: #64748b; margin: 0 0 22px; line-height: 1.65;
  }
  .dark-m .ct-sub { color: #6b7280; }

  /* ── Campaign Badge ── */
  .ct-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 14px; border-radius: 40px;
    background: #fff1f2; border: 1.5px solid #fca5a5;
    font-size: 13px; font-weight: 600; color: #ef4444;
    margin-bottom: 18px;
  }
  .dark-m .ct-badge { background: #2a1a1a; border-color: #7f1d1d; color: #fca5a5; }

  /* ── Cost Row ── */
  .ct-cost {
    display: inline-flex; align-items: center; gap: 6px;
    background: #f8fafc; border: 1.5px solid #e2e8f0;
    border-radius: 10px; padding: 8px 16px;
    font-size: 13px; color: #64748b; margin-bottom: 24px;
  }
  .dark-m .ct-cost { background: #151827; border-color: #2d3248; color: #9ca3af; }
  .ct-cost strong { color: #0f172a; font-weight: 700; }
  .dark-m .ct-cost strong { color: #f1f5f9; }

  /* ── Divider ── */
  .ct-divider {
    height: 1px; background: #e2e8f0; margin: 0 0 24px;
  }
  .dark-m .ct-divider { background: #2a2f45; }

  /* ── Buttons Row ── */
  .ct-actions { display: flex; gap: 10px; }

  .ct-cancel {
    flex: 1; padding: 13px 0; border-radius: 12px;
    border: 1.5px solid #e2e8f0; background: #f8fafc;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    letter-spacing: 0.06em; text-transform: uppercase;
    color: #475569; cursor: pointer; transition: all 0.2s;
  }
  .ct-cancel:hover { background: #f1f5f9; border-color: #94a3b8; color: #1e293b; transform: translateY(-1px); }
  .dark-m .ct-cancel { background: #252d45; border-color: #2d3248; color: #9ca3af; }
  .dark-m .ct-cancel:hover { background: #2e3860; border-color: #3b82f6; color: #e2e8f0; }

  .ct-confirm {
    flex: 1; padding: 13px 0; border-radius: 12px; border: none;
    background: linear-gradient(135deg, #dc2626, #b91c1c);
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    letter-spacing: 0.06em; text-transform: uppercase;
    color: #fff; cursor: pointer; transition: all 0.2s;
    box-shadow: 0 4px 16px rgba(220,38,38,0.32);
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .ct-confirm:hover { background: linear-gradient(135deg,#ef4444,#dc2626); box-shadow: 0 6px 22px rgba(220,38,38,0.42); transform: translateY(-1px); }
  .ct-confirm:disabled { background: #e2e8f0; color: #94a3b8; box-shadow: none; transform: none; cursor: not-allowed; }
  .dark-m .ct-confirm:disabled { background: #252d45; color: #4b5563; }

  /* ── Spinner ── */
  .ct-spin {
    width: 15px; height: 15px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff; border-radius: 50%;
    animation: ct-spin 0.65s linear infinite;
    flex-shrink: 0;
  }
  @keyframes ct-spin { to { transform: rotate(360deg); } }

  .ct-spin-green {
    width: 28px; height: 28px;
    border: 3px solid rgba(22,163,74,0.2);
    border-top-color: #16a34a; border-radius: 50%;
    animation: ct-spin 0.7s linear infinite;
    margin: 0 auto;
  }

  @media (max-width: 520px) {
    .ct-card { padding: 30px 20px 26px; border-radius: 18px; }
    .ct-title { font-size: 18px; }
    .ct-icon-wrap { width: 64px; height: 64px; }
    .ct-actions { flex-direction: column; }
  }
`;

let injected = false;
const injectCSS = () => {
  if (injected) return;
  injected = true;
  const el = document.createElement('style');
  el.innerHTML = CSS;
  document.head.appendChild(el);
};

/* ─── Delete Modal ───────────────────────────────────────────────────────── */
const DeleteModal = ({ isDark, campaign, onClose, onConfirm, loading, success }) =>
  createPortal(
    <div
      className="ct-overlay"
      onClick={(e) => { if (!loading && !success && e.target === e.currentTarget) onClose(); }}
    >
      {success ? (
        /* ── Success State ── */
        <div className={`ct-card ${isDark ? 'dark-m' : ''}`}>
          <div className="ct-icon-wrap success-i">
            <IconCheck />
          </div>
          <p className={`ct-title success-color`}>Deleted Successfully!</p>
          <p className="ct-sub">
            Campaign has been removed from the system.<br />Redirecting…
          </p>
          <div className="ct-spin-green" />
        </div>
      ) : (
        /* ── Confirm State ── */
        <div className={`ct-card ${isDark ? 'dark-m' : ''}`}>

          <button className="ct-close" onClick={onClose} disabled={loading}>
            <IconClose />
          </button>

          <div className="ct-icon-wrap danger">
            <IconWarning />
          </div>

          <p className="ct-title">Delete Campaign?</p>
          <p className="ct-sub">
            This will permanently remove the campaign<br />from the system and cannot be undone.
          </p>

          {/* Campaign badge */}
          {campaign?.CampainName && (
            <div>
              <span className="ct-badge">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                </svg>
                {campaign.CampainName}
              </span>
            </div>
          )}

          {/* Cost */}
          {campaign?.Cost && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '22px' }}>
              <div className="ct-cost">
                <span>Campaign Cost:</span>
                <strong>${parseFloat(campaign.Cost || 0).toFixed(2)}</strong>
              </div>
            </div>
          )}

          <div className="ct-divider" />

          <div className="ct-actions">
            <button className="ct-cancel" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button className="ct-confirm" onClick={onConfirm} disabled={loading}>
              {loading
                ? <><div className="ct-spin" /><span>Deleting…</span></>
                : <><IconTrash /><span>Delete</span></>}
            </button>
          </div>

        </div>
      )}
    </div>,
    document.body
  );

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const CampaignTable = ({ campaign, AllCampaigns }) => {
  injectCSS();

  const theme    = useTheme();
  const isDark   = theme.palette.mode === 'dark';
  const dispatch = useDispatch();

  const [show,    setShow]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const deleteCampaign = async () => {
    setLoading(true);
    try {
      await dispatch(UpdateOneCampaignData(campaign._id, { campaignisactivatedelete: false }));
      setLoading(false);
      setSuccess(true);
      Notify('success', 'Campaign deleted successfully', 'success');
      setTimeout(() => {
        setShow(false);
        setSuccess(false);
        AllCampaigns();
      }, 1500);
    } catch {
      Notify('error', 'Failed to delete campaign', 'error');
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── Action Buttons ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Link
          to={`/updatecampaign/${campaign._id}`}
          className={`ct-btn edit ${isDark ? 'dark-btn-edit' : ''}`}
          title="Edit campaign"
        >
          <IconEdit />
        </Link>
        <button
          className={`ct-btn del ${isDark ? 'dark-btn-del' : ''}`}
          title="Delete campaign"
          onClick={() => setShow(true)}
        >
          <IconTrash />
        </button>
      </div>

      {/* ── Portal Modal ── */}
      {show && (
        <DeleteModal
          isDark={isDark}
          campaign={campaign}
          onClose={() => { if (!loading && !success) setShow(false); }}
          onConfirm={deleteCampaign}
          loading={loading}
          success={success}
        />
      )}
    </>
  );
};
